/**
 * Crosshair component renders vertical line at hover position.
 * Subscribes to 'hover' event only for high-frequency updates.
 * Includes moving axis labels for X (time) and Y (price) values.
 *
 * Uses core/hoverUtils for pure interpolation logic.
 * This component is a thin visual wrapper.
 */

import React, { useCallback } from "react";

import styles from "../styles/styles.module.css";
import { useChartContext } from "../core/ChartContext";
import {
  calculateCandlestickDotY,
  DEFAULT_CHART_COLOR,
  formatPrice,
  formatTimestamp,
  getFirstSeriesCurrency,
  getTimePrecision,
  interpolateYPosition,
  intervalToPrecision,
} from "~utils/ticker-chart";
import { useChartSubscription } from "~hooks/ticker-chart";
import { isOhlcvPoint, type OhlcvPoint, type PricePoint } from "~types/types";

export interface CrosshairProps {
  /** Show vertical line, default: true */
  showVertical?: boolean;
  /** Show horizontal line, default: false */
  showHorizontal?: boolean;
  /** Show X-axis label at crosshair position, default: true */
  showXLabel?: boolean;
  /** Show Y-axis label at crosshair position, default: true */
  showYLabel?: boolean;
  /** Locale for formatting */
  locale?: string;
  /** Custom y-axis formatter function */
  yAxisFormatter?: (value: number, currency?: string) => string;
  /** Threshold for compact notation (e.g., 1K, 1M), default: 10000 */
  compactThreshold?: number;
}

/**
 * Crosshair renders cursor position indicators.
 * Updates at 60fps without re-rendering other components.
 */
const CrosshairInner: React.FC<CrosshairProps> = ({
  showVertical = true,
  showHorizontal = false,
  showXLabel = true,
  showYLabel = true,
  locale = "en-US",
  yAxisFormatter,
  compactThreshold = 10000,
}) => {
  const store = useChartContext();

  // Subscribe to hover only - scales/viewport are read synchronously from store
  useChartSubscription(store, "hover");

  const hover = store.getHoverState();
  const state = store.getState();
  const layout = store.getLayout();
  const scales = store.getScales();
  const viewport = store.getViewport();

  const scale = scales.y;
  const isComparisonMode = state.mode === "comparison";

  // Memoize calculateDotY to avoid creating new function every render
  const calculateDotY = useCallback(
    (
      seriesId: string,
      point: PricePoint | OhlcvPoint | null
    ): number | null => {
      if (!point) return null;

      const currentScales = store.getScales();
      const currentHover = store.getHoverState();
      const currentState = store.getState();
      const chartType = currentState.chartType;

      // For OHLC/candlestick charts, follow cursor Y clamped to candle range
      if (chartType === "candlestick" && isOhlcvPoint(point)) {
        return calculateCandlestickDotY(
          point,
          currentHover.chartY,
          currentScales.y
        );
      }

      // For comparison mode, interpolate the Y value at the snapped timestamp
      if (currentState.mode === "comparison") {
        const visibleData = store.getVisibleData(seriesId);

        if (!visibleData.length) {
          return null;
        }

        // Use core function for interpolation
        const result = interpolateYPosition(
          visibleData,
          point.timestamp,
          currentScales.y
        );

        return result.y;
      }

      // For single mode line charts, use the actual price value
      const priceValue = "value" in point ? point.value : point.close;

      return priceValue !== undefined
        ? currentScales.y.scale(priceValue)
        : null;
    },
    [store]
  );

  // Get all data points from hover state for each series
  const nearestPoints = hover.nearestPoints
    ? [...hover.nearestPoints.entries()]
    : [];

  // Get first point to determine if we should render anything
  const firstEntry = nearestPoints[0];
  const firstPoint = firstEntry ? firstEntry[1] : null;

  if (!hover.visible || !firstPoint) {
    return null;
  }

  // Use the actual data point's timestamp for snapping (not cursor timestamp)
  const dataPointTimestamp = firstPoint.timestamp;

  // Check if the data point is still within the visible viewport
  if (
    dataPointTimestamp < viewport.start ||
    dataPointTimestamp > viewport.end
  ) {
    return null;
  }

  // Vertical line snaps to the data point's X position
  const snappedX = scales.x.scale(dataPointTimestamp);

  const verticalLine = {
    x: snappedX,
    y1: layout.priceArea.y,
    y2: layout.volumeArea
      ? layout.volumeArea.y + layout.volumeArea.height
      : layout.priceArea.y + layout.priceArea.height,
  };

  // Get series metadata to access colors
  const seriesMeta = state.seriesMeta;

  // Get currency from first series
  const seriesCurrency = getFirstSeriesCurrency(seriesMeta);

  // In comparison mode, don't show currency symbol
  const displayCurrency = isComparisonMode ? undefined : seriesCurrency;

  const domain = scale.domain();
  const [min, max] = domain;

  // Use compact notation for large values (1K, 1M, 1B)
  const useCompact =
    Math.abs(max) >= compactThreshold || Math.abs(min) >= compactThreshold;

  // Calculate Y value for horizontal line label (price at cursor position)
  const yValue = scales.y.invert(hover.chartY);
  const yLabelText = yAxisFormatter
    ? yAxisFormatter(yValue, displayCurrency)
    : formatPrice(yValue, {
        currency: displayCurrency,
        locale,
        compact: useCompact,
      });

  // Calculate X value for vertical line label (timestamp at snapped position)
  // Use interval to derive precision when available, otherwise fall back to range
  const interval = state.dataConfig?.interval;
  const timeRange = viewport.end - viewport.start;
  const precision = interval
    ? intervalToPrecision(interval)
    : getTimePrecision(timeRange);
  const xLabelText = formatTimestamp(
    dataPointTimestamp,
    precision,
    locale,
    true
  );

  // Get typography from layout (scales with chart size)
  const { axisLabelSize, secondaryLabelSize, charWidth } = layout.typography;

  // Y-axis label positioning (determine from layout - right if yAxisArea starts after priceArea)
  const yAxisPosition =
    layout.yAxisArea.x > layout.priceArea.x ? "right" : "left";

  // Padding between chart edge and label background
  const Y_AXIS_LABEL_GAP = 2;

  // Y label background dimensions - sized based on font
  const Y_LABEL_PADDING_X = Math.round(axisLabelSize * 0.5);
  const Y_LABEL_PADDING_Y = Math.round(axisLabelSize * 0.3);
  const Y_LABEL_HEIGHT = axisLabelSize;
  const estimatedYLabelWidth =
    yLabelText.length * charWidth + Y_LABEL_PADDING_X * 2;

  // Position label with gap from chart edge
  const yLabelBgX =
    yAxisPosition === "right"
      ? layout.priceArea.x + layout.priceArea.width + Y_AXIS_LABEL_GAP
      : layout.priceArea.x - estimatedYLabelWidth - Y_AXIS_LABEL_GAP;

  // Clamp Y position to keep label within chart vertical bounds
  const yLabelBgHeight = Y_LABEL_HEIGHT + Y_LABEL_PADDING_Y * 2;
  const yLabelBgYUnclamped = hover.chartY - yLabelBgHeight / 2;
  const yLabelBgY = Math.max(
    layout.priceArea.y,
    Math.min(
      yLabelBgYUnclamped,
      layout.priceArea.y + layout.priceArea.height - yLabelBgHeight
    )
  );

  // Text position - centered horizontally in background rect
  const yLabelTextX = yLabelBgX + estimatedYLabelWidth / 2;

  // X-axis label positioning - text aligned with secondary label row (date/year labels)
  // This matches the secondaryLabelY calculation in XAxis component
  const primaryLabelY = layout.xAxisArea.y + axisLabelSize + 4;
  const xLabelTextY = primaryLabelY + secondaryLabelSize + 4;

  // Estimate label width for bounds checking
  const estimatedXLabelWidth = xLabelText.length * charWidth;

  // X label background dimensions - sized based on font
  const X_LABEL_PADDING_X = Math.round(secondaryLabelSize * 0.5);
  const X_LABEL_PADDING_Y = Math.round(secondaryLabelSize * 0.5);
  const X_LABEL_HEIGHT = secondaryLabelSize;
  const xLabelBgWidth = estimatedXLabelWidth + X_LABEL_PADDING_X * 2;
  const xLabelBgHeight = X_LABEL_HEIGHT + X_LABEL_PADDING_Y - 2;

  // Clamp X label background position to stay within chart bounds
  // The background rect must not have negative x or extend beyond chart width
  const xLabelBgX = Math.max(
    0,
    Math.min(
      snappedX - xLabelBgWidth / 2,
      layout.priceArea.x + layout.priceArea.width - xLabelBgWidth
    )
  );

  // Center text within the clamped background rect
  const xLabelX = xLabelBgX + xLabelBgWidth / 2;

  // Rect extends upward from text baseline - text stays at xLabelTextY
  const xLabelBgY = xLabelTextY - X_LABEL_HEIGHT / 2 - X_LABEL_PADDING_Y;

  return (
    <g
      className={styles.crosshair}
      data-component="price-movement-chart-crosshair"
    >
      {/* Vertical line - snapped to data point X */}
      {showVertical && (
        <line
          className={styles.crosshairVertical}
          x1={verticalLine.x}
          y1={verticalLine.y1}
          x2={verticalLine.x}
          y2={xLabelBgY - 4}
          pointerEvents="none"
          data-component="price-movement-chart-crosshair-xaxis-line"
        />
      )}

      {/* Horizontal line at cursor Y position */}
      {showHorizontal && (
        <line
          className={styles.crosshairHorizontal}
          x1={layout.priceArea.x}
          y1={hover.chartY}
          x2={layout.priceArea.x + layout.priceArea.width}
          y2={hover.chartY}
          pointerEvents="none"
          data-component="price-movement-chart-crosshair-yaxis-line"
        />
      )}

      {/* Circular indicator for each ticker on price line path */}
      {nearestPoints.map(([seriesId, point]) => {
        if (!point) return null;

        const dotY = calculateDotY(seriesId, point);

        if (dotY === null) return null;

        // Determine dot color based on chart mode:
        // - Single mode: theme-based (black for light, white for dark)
        // - Comparison mode: palette color from series metadata
        let dotColor: string;

        if (isComparisonMode) {
          const series = seriesMeta.get(seriesId);

          dotColor = series?.color ?? DEFAULT_CHART_COLOR;
        } else {
          // Single mode: use theme-based color
          dotColor = state.theme === "dark" ? "#ffffff" : "#000000";
        }

        return (
          <circle
            key={seriesId}
            cx={snappedX}
            cy={dotY}
            r={4}
            className={styles.crosshairDot}
            fill={dotColor}
            pointerEvents="none"
          />
        );
      })}

      {/* Moving Y-axis label (price) */}
      {showYLabel && (
        <g
          className={styles.crosshairYLabel}
          pointerEvents="none"
          data-component="price-movement-chart-crosshair-yaxis-label"
        >
          <rect
            className={styles.crosshairLabelBg}
            x={yLabelBgX}
            y={yLabelBgY}
            width={estimatedYLabelWidth}
            height={yLabelBgHeight}
            rx={2}
            ry={2}
          />
          <text
            className={styles.crosshairLabelText}
            x={yLabelTextX}
            y={yLabelBgY + yLabelBgHeight / 2}
            dy="0.35em"
            textAnchor="middle"
            fontSize={axisLabelSize}
          >
            {yLabelText}
          </text>
        </g>
      )}

      {/* Moving X-axis label (time) */}
      {showXLabel && (
        <g
          className={styles.crosshairXLabel}
          pointerEvents="none"
          data-component="price-movement-chart-crosshair-xaxis-label"
        >
          <rect
            className={styles.crosshairLabelBg}
            x={xLabelBgX}
            y={xLabelBgY}
            width={xLabelBgWidth}
            height={xLabelBgHeight}
            rx={2}
            ry={2}
          />
          <text
            className={styles.crosshairLabelText}
            x={xLabelX}
            y={xLabelTextY}
            textAnchor="middle"
            fontSize={secondaryLabelSize}
          >
            {xLabelText}
          </text>
        </g>
      )}
    </g>
  );
};

export const Crosshair = React.memo(CrosshairInner);
Crosshair.displayName = "Crosshair";
